// Logout here is client-only — there's no backend call to revoke the token, so it stays
// valid server-side until it naturally expires. This list can't stop that token being reused
// via curl/Postman/another browser/another device (those never touch this code at all), but
// it does close the narrower gap of someone pasting the same token back into this browser's
// localStorage after logging out here — AuthGuard checks it via isTokenRevoked below.
const REVOKED_TOKENS_KEY = "revokedTokens";
const MAX_REVOKED_TOKENS = 5;

export function signOut() {
  const outgoingToken = localStorage.getItem("authToken");
  if (outgoingToken) {
    try {
      const revoked: string[] = JSON.parse(localStorage.getItem(REVOKED_TOKENS_KEY) || "[]");
      const next = [outgoingToken, ...revoked.filter((t) => t !== outgoingToken)].slice(0, MAX_REVOKED_TOKENS);
      localStorage.setItem(REVOKED_TOKENS_KEY, JSON.stringify(next));
    } catch {
      localStorage.setItem(REVOKED_TOKENS_KEY, JSON.stringify([outgoingToken]));
    }
  }

  localStorage.removeItem("vouchlink_waitlist");
  localStorage.removeItem("vouchlink_phone");
  localStorage.removeItem("verifyPhone");
  localStorage.removeItem("authToken");
  localStorage.removeItem("referralCode");
  localStorage.removeItem("userDetails");
  localStorage.removeItem("balance");
  localStorage.removeItem("user-type");
  sessionStorage.removeItem("seenBankPopup")
  localStorage.removeItem("redirectAfterLogin")
}

// See the note above signOut — this only catches a logged-out token reused in this same
// browser, not real revocation.
export function isTokenRevoked(token: string): boolean {
  try {
    const revoked: string[] = JSON.parse(localStorage.getItem(REVOKED_TOKENS_KEY) || "[]");
    return revoked.includes(token);
  } catch {
    return false;
  }
}

// Pre-login pages a post-login redirect should never send the user back to. Without this,
// a background 401 (e.g. some unrelated authenticated fetch firing while the user happens to
// be sitting on /login) stores "redirectAfterLogin" = "/login" — then a *successful* login
// right after reads that stale value back and navigates to "/login" again, a no-op that looks
// like the login silently failed even though the token was saved correctly. The very next
// login attempt works, because by then the bad value has been consumed and cleared.
const PRE_LOGIN_PATH_PREFIXES = ["/login", "/signup", "/onboarding", "/advertiser/signup"];

export function isPreLoginPath(pathname: string): boolean {
  return PRE_LOGIN_PATH_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

// Shared by both request hooks (use-req.tsx, req.tsx): a 401 almost always means the token is
// missing, invalid, or stale — notably, logging in on a second tab overwrites the shared
// localStorage token, which silently 401s every request still in flight on the first tab.
// Previously that first tab was left stranded: every subsequent API call just kept failing
// with no recovery short of a manual refresh. This forces an actual logout (clear storage,
// redirect to /login) the moment any request comes back unauthorized, same as a deliberate
// logout, so the user lands back on login instead of a silently broken page.
let loggingOutFrom401 = false;
export function forceLogoutOn401() {
  if (loggingOutFrom401) return;
  // Expected to 401 harmlessly before a session exists (e.g. a background call firing while
  // still on /login) — not a reason to force a redirect that's already where the user is.
  if (isPreLoginPath(window.location.pathname)) return;
  if (!localStorage.getItem("authToken")) return;

  loggingOutFrom401 = true;
  const currentPath = window.location.pathname;
  signOut();
  // Set after signOut (which clears it) so the user lands back where they were once logged in
  // again, same behavior the old per-hook 401 handling already aimed for.
  localStorage.setItem("redirectAfterLogin", currentPath);
  window.location.href = "/login";
}

// Pulls a human-readable message out of the shapes this backend's error responses actually
// come in — plain `{message}`/`{detail: "string"}`, and FastAPI/Pydantic's validation-error
// shape `{detail: [{msg: "..."}]}` (confirmed via a real 422: a cross-field reward validation
// error, e.g. "daily_click_limit (2) must be at least min_clicks_per_reward (5)", was landing
// in `detail[0].msg` and getting silently dropped by callers that only checked `response.message`).
export function getApiErrorMessage(error: any, fallback: string): string {
  if (!error) return fallback;
  if (typeof error.detail === 'string') return error.detail;
  if (Array.isArray(error.detail) && error.detail[0]) {
    return error.detail[0].msg || error.detail[0].message || fallback;
  }
  return error.message || fallback;
}

// Client-side login-attempt throttling (pentest finding: no account lockout after repeated
// failed logins — CWE-307). IMPORTANT: this is a UX-layer safeguard only, not the actual fix.
// It only limits attempts made through this app's own login form; it does nothing against
// direct API attacks (e.g. Burp Intruder hitting /auth/auth/login directly, which is how the
// finding was originally demonstrated) since those never touch this code. The real fix has to
// be server-side rate limiting / lockout on the login endpoint — this just keeps a legitimate
// user (or someone brute-forcing through the UI specifically) from hammering the form.
const LOGIN_LOCKOUT_KEY = "loginLockoutAttempts";
export const LOGIN_MAX_ATTEMPTS = 5;
export const LOGIN_LOCKOUT_MS = 30 * 60 * 1000; // 30 minutes

export interface LoginLockoutEntry {
  failCount: number;
  lockedUntil: number | null;
}

const getLoginLockoutMap = (): Record<string, LoginLockoutEntry> => {
  try {
    return JSON.parse(localStorage.getItem(LOGIN_LOCKOUT_KEY) || "{}");
  } catch {
    return {};
  }
};

// Keyed per email/phone (lowercased) — a lockout on one account doesn't block login attempts
// for a different account from the same browser.
export const getLoginLockout = (identifier: string): LoginLockoutEntry => {
  const key = identifier?.trim().toLowerCase();
  if (!key) return { failCount: 0, lockedUntil: null };
  return getLoginLockoutMap()[key] || { failCount: 0, lockedUntil: null };
};

export const recordFailedLogin = (identifier: string): LoginLockoutEntry => {
  const key = identifier?.trim().toLowerCase();
  if (!key) return { failCount: 0, lockedUntil: null };

  const map = getLoginLockoutMap();
  const current = map[key] || { failCount: 0, lockedUntil: null };

  // Once already locked, keep the existing lockedUntil rather than extending it on every
  // further attempt — a fixed 30-minute window, not a rolling one.
  if (current.lockedUntil && current.lockedUntil > Date.now()) {
    return current;
  }

  const failCount = current.failCount + 1;
  const lockedUntil = failCount >= LOGIN_MAX_ATTEMPTS ? Date.now() + LOGIN_LOCKOUT_MS : null;
  const next: LoginLockoutEntry = { failCount: lockedUntil ? 0 : failCount, lockedUntil };

  map[key] = next;
  localStorage.setItem(LOGIN_LOCKOUT_KEY, JSON.stringify(map));
  return next;
};

export const clearLoginLockout = (identifier: string) => {
  const key = identifier?.trim().toLowerCase();
  if (!key) return;
  const map = getLoginLockoutMap();
  delete map[key];
  localStorage.setItem(LOGIN_LOCKOUT_KEY, JSON.stringify(map));
};

// Some admin endpoints send the literal string "Null" (not a real null) when no document has been uploaded.
export function hasDocumentUrl(value: string | null | undefined): value is string {
  return !!value && value.trim().toLowerCase() !== "null";
}

const API_BASE_URL = "https://dev.api.vouchlinks.com/api/v1";

// Links on our own API (e.g. the advertiser documents the admin endpoints now return as
// "https://dev.api.vouchlinks.com/api/v1/advertiser_docs/...") need the user's Bearer token —
// opened directly in a tab they fail with "missing_token" (a tab can't send headers, and the
// backend doesn't accept the token as a query param). Bare keys resolve against the API too.
function isOwnApiRef(value: string): boolean {
  return !/^https?:\/\//i.test(value) || value.startsWith(API_BASE_URL);
}

function ownApiUrl(value: string): string {
  return /^https?:\/\//i.test(value) ? value : `${API_BASE_URL}/${value.replace(/^\/+/, "")}`;
}

function documentRefFileName(ref: string): string {
  try {
    const path = ref.split("?")[0];
    return decodeURIComponent(path.substring(path.lastIndexOf("/") + 1)) || "";
  } catch {
    return "";
  }
}

// View a private verification document in a new tab.
//
// Confirmed: a real, absolute URL (the presigned S3 link the upload/detail endpoints hand back,
// or any other full URL) opens correctly via direct navigation — pasting the same link into a
// new tab works — while fetching it from JS first can fail (CORS, or a 400 from S3 rejecting an
// Authorization header alongside its own query signature) even though the link itself is fine.
// So any absolute URL is opened as-is; only a bare storage key (no scheme) goes through our own
// API with the admin's token.
export async function openPrivateDocument(ref: string): Promise<void> {
  const value = (ref || "").trim();
  if (!value) throw new Error("No document to open");

  if (!isOwnApiRef(value)) {
    window.open(value, "_blank", "noopener,noreferrer");
    return;
  }

  // Our API needs the Bearer header, which only a fetch can send — load it, then show it.
  const preview = await loadDocumentPreview(value);
  window.open(preview.src, "_blank", "noopener,noreferrer");
}

const IMAGE_EXTENSIONS = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;

export interface DocumentPreview {
  src: string;
  isImage: boolean;
  // Frees the object URL for a document fetched from our API; no-op for a direct link.
  release: () => void;
}

// Prepares a document for in-page display (an <img> or <iframe> in a modal, so viewing never
// leaves the page). Our own API's links are fetched with the Bearer token and shown from memory;
// any other link (e.g. a signed S3 URL) is displayed directly.
export async function loadDocumentPreview(ref: string): Promise<DocumentPreview> {
  const value = (ref || "").trim();
  if (!value) throw new Error("No document to open");
  const path = value.split("?")[0];

  if (!isOwnApiRef(value)) {
    return { src: value, isImage: IMAGE_EXTENSIONS.test(path), release: () => {} };
  }

  const authToken = localStorage.getItem("authToken");
  const res = await fetch(ownApiUrl(value), {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
  });
  if (!res.ok) throw new Error(`Failed to load document (${res.status})`);
  let blob = await res.blob();
  // A generic content type would make the browser download a PDF instead of rendering it.
  if (!blob.type || blob.type === "application/octet-stream") {
    const type = /\.pdf$/i.test(path) ? "application/pdf" : IMAGE_EXTENSIONS.test(path) ? `image/${path.split(".").pop()!.toLowerCase().replace("jpg", "jpeg")}` : blob.type;
    blob = new Blob([blob], { type });
  }
  const src = URL.createObjectURL(blob);
  return { src, isImage: blob.type.startsWith("image/"), release: () => URL.revokeObjectURL(src) };
}

// Download a private verification document (save it, rather than navigate to it).
//
// For our own API this pulls the bytes and forces a save via an anchor's `download` attribute.
// For a presigned storage URL there's no way to force "Save As" from the frontend alone (that
// needs a `response-content-disposition: attachment` param baked into the URL server-side, and
// fetching it cross-origin commonly fails on the bucket's CORS policy) — so it falls back to
// just opening it, same as View, letting the browser's own viewer save it.
export async function downloadPrivateDocument(ref: string, fileName?: string): Promise<void> {
  const value = (ref || "").trim();
  if (!value) throw new Error("No document to download");

  const ownApi = isOwnApiRef(value);
  const authToken = localStorage.getItem("authToken");
  const url = ownApi ? ownApiUrl(value) : value;

  try {
    const res = await fetch(url, {
      headers: ownApi && authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
    });
    if (!res.ok) throw new Error(`status ${res.status}`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName || documentRefFileName(value) || "document";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (error) {
    // An authenticated link opened in a tab would just fail with "missing_token" — report it.
    if (ownApi) throw error instanceof Error ? new Error(`Failed to download document (${error.message})`) : error;
    // Couldn't pull the bytes directly (usually a CORS restriction on the storage bucket) —
    // open it so the user can still save it from the browser's viewer.
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

export type AdvertiserDocumentType = "company_registration_certificate" | "directors_government_id";

// Advertiser verification documents come back from the admin endpoints as bare storage keys
// (e.g. "advertiser_docs/company_registration_certificate/<uuid>.jpeg"), which can't be opened
// directly — GET /super-admin/advertisers/{id}/documents/{doc_type} exchanges one for a
// short-lived signed link. A full URL (e.g. returned right after an admin upload) is used as-is.
export async function resolveAdvertiserDocumentLink(
  advertiserId: number | string,
  docType: AdvertiserDocumentType,
  ref: string
): Promise<string> {
  if (/^https?:\/\//i.test(ref)) return ref;
  const authToken = localStorage.getItem("authToken");
  const res = await fetch(`${API_BASE_URL}/super-admin/advertisers/${advertiserId}/documents/${docType}`, {
    headers: authToken ? { Authorization: `Bearer ${authToken}` } : undefined,
  });
  const data = res.ok ? await res.json().catch(() => null) : null;
  const link = data?.preview_url || data?.url || data?.download_url;
  if (!link) throw new Error("This document can't be opened yet — the server didn't return a viewable link.");
  return link;
}

export async function loadAdvertiserDocumentPreview(
  advertiserId: number | string,
  docType: AdvertiserDocumentType,
  ref: string
): Promise<DocumentPreview> {
  return loadDocumentPreview(await resolveAdvertiserDocumentLink(advertiserId, docType, ref));
}

export async function downloadAdvertiserDocument(
  advertiserId: number | string,
  docType: AdvertiserDocumentType,
  ref: string,
  fileName?: string
): Promise<void> {
  const link = await resolveAdvertiserDocumentLink(advertiserId, docType, ref);
  await downloadPrivateDocument(link, fileName || documentRefFileName(ref));
}

// No backend field for this yet, so — same as the daily payout limit
// elsewhere in the app — link generation is tracked client-side as a
// per-day counter in localStorage.
export const DAILY_LINK_LIMIT = 10;

export function getLinksGeneratedToday(): number {
  const todayKey = new Date().toDateString();
  const stored = JSON.parse(localStorage.getItem("dailyLinks") || "{}");
  return stored[todayKey] || 0;
}

export function hasReachedDailyLinkLimit(): boolean {
  return getLinksGeneratedToday() >= DAILY_LINK_LIMIT;
}

export function recordLinkGenerated(): void {
  const todayKey = new Date().toDateString();
  const stored = JSON.parse(localStorage.getItem("dailyLinks") || "{}");
  stored[todayKey] = (stored[todayKey] || 0) + 1;
  localStorage.setItem("dailyLinks", JSON.stringify(stored));
}

// Where a logged-in user belongs, based on their "user-type" — mirrors the
// switch-case used at login time in src/pages/auth/login-form.tsx.
export function getDashboardPathForRole(userType: string | null | undefined): string {
  switch (userType) {
    case "SUPER_ADMIN":
      return "/admin/overview";
    case "ADVERTISER":
      return "/business/profile";
    case "RECOMMENDER":
      return "/";
    default:
      return "/login";
  }
}

export const getBaseUrl = () => {
  if (typeof window === "undefined") {
    return "https://prod.vouchlinks.com";
  }

  const hostname = window.location.hostname;

  if (hostname.includes("prod.")) {
    // If running on prod.vouchlinks.com → use prod.vouchlinks.com (live)
    return "https://prod.vouchlinks.com";
  }

  // If running anywhere else (localhost, staging, live) → stay on vouchlinks.com
  if (hostname.includes("vouchlinks.com")) {
    return "https://vouchlinks.com";
  }

  // Default (local/staging/dev)
  return "https://prod.vouchlinks.com";
};

export function formatCurrency(
  value: number | undefined | null,
  currency?: string,
  display?: "code" | "symbol"
) {
  try {
    if (value != null) {
      return value.toLocaleString("en-NG", {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
        currencyDisplay: display || undefined,
      });
    } else {
      return "0";
    }
  } catch (e) {

    return "N/A";
  }
}


export function formatDateLong(date: Date): string {
  if (isNaN(date.getTime())) return 'Invalid date';

  const options: Intl.DateTimeFormatOptions = {
    month: "long",
    day: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  return date
    .toLocaleString("en-US", options)
    // .toUpperCase()
    .replace(",", "")
    // Modern ICU inserts "at" between the date and time (e.g. "September 18, 2026 at 11:18
    // AM") — stripped for a plain "Month Day Year Time" format, consistent with the comma
    // already being stripped above.
    .replace(" at ", " ");
}

// Bank account numbers come back from the API in full (e.g. "8033070445") — mask all but
// the last 4 digits for display, e.g. "••••0445".
export function maskAccountNumber(accountNumber: string | null | undefined): string {
  if (!accountNumber) return "";
  const digits = accountNumber.trim();
  if (digits.length <= 4) return digits;
  return `••••${digits.slice(-4)}`;
}

